import { selectUserId } from "@common-reducers/UserSelectors";
import { withoutOversizedIn } from '@common-reducers/in-query';
import { getRuntimeUrl } from '@common-services/storage';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import React, { DetailedHTMLProps, JSXElementConstructor, PropsWithChildren, useCallback, useEffect, useMemo, useRef } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';
import { advanceServerOffset } from './pagination-offset';
import { buildLazyLoadSort } from './lazy-loading-sort';

type Props = {
    height: number | string;
    rowHeightString?: string;
    filterObj: any;
    DBThunks: any;
    DBReducer: (state: any) => any;
    selectByQuery: (state: any, filterObj: any, projectionArray: string[], sortObj: any) => any;
    selector?: (state: any) => any;
    Component: JSXElementConstructor<any>;
    isFullWidth?: boolean;
    featchData?: boolean;
    findObj?: any;
    sortOptions?: { field: string; order: 'asc' | 'desc'; statusFilter?: string | null };
};

const LazyLoadingVirtuoso = ({
    height, rowHeightString = 'auto', DBThunks, DBReducer, selectByQuery, Component, filterObj, isFullWidth = false, selector, featchData = true, findObj = {}, sortOptions
}: Props) => {
    const dispatch = useAppDispatch();
    // FE#157: the server pagination offset, reconciled from the SERVER's returned
    // page counts rather than the client list length (which drifts and drops rows).
    const fetchedOffsetRef = useRef(0);
    const isFetchingRef = useRef(false);
    const { t } = useTranslation(namespace);
    const noAudiencesFoundImgSrc = getRuntimeUrl("/img/no-audiences-found.svg");

    const ownerId = useAppSelector(selectUserId);

    // Call sites pass filterObj/findObj/sortOptions as inline object literals,
    // so their identity changes on every parent render. Key the memos/effects
    // on a stable serialization to avoid re-fetch storms.
    const filterKey = JSON.stringify(filterObj ?? {});
    const findKey = JSON.stringify(findObj ?? {});
    const sortKey = JSON.stringify(sortOptions ?? null);

    const query = useMemo(() => {
        const baseQuery = {
            ...(filterObj ?? {}),
        };
        // Only add owner filter if ownerId is defined
        if (ownerId) {
            baseQuery.owner = ownerId;
        }
        return baseQuery;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterKey, ownerId]);
    const isItemsLoaded = useAppSelector(state => DBReducer(state)?.itemsLoaded);

    const fliterdList = useAppSelector((state) => {
        if (selector) {
            return selector(state);
        } else {
            // Build query object, only including owner if it's defined
            const queryObj = {
                deleted: false,
                ...query,
            };
            // Only add owner if it's defined to avoid filtering with undefined
            if (ownerId) {
                queryObj.owner = ownerId;
            }

            // FE#257: build the sort ONCE (via buildLazyLoadSort) and reuse the
            // exact same object for the paginated fetch below, so the loaded
            // window is always a true prefix of what is displayed here.
            const sortObj = buildLazyLoadSort(sortOptions);

            const rawList = selectByQuery(DBReducer(state), queryObj,
                [],
                sortObj
            );
            
            return rawList;
        }
    });

    const resolvedHeight = typeof height === 'number' ? `${height}px` : height;
    // When a percentage/relative height is requested, the wrapper must take full height
    // of its (flex-sized) parent so the virtuoso can resolve `height: 100%` against it.
    const isRelativeHeight = typeof height === 'string' && height.includes('%');

    const HeightPreservingItem = useCallback(
        ({
            children,
            ...props
        }: PropsWithChildren<
            DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>
        >) => {
            return (
                <div
                    {...props}
                    className="height-preserving-container"
                    style={{ height: rowHeightString }}
                >
                    {children}
                </div>
            );
        },
        [rowHeightString]
    );

    const loadMore = () => {
        if (!featchData || isFetchingRef.current) return; // guard overlapping endReached fires

        // FE#257: identical to the display sort above so pages compose in the
        // same order the list shows.
        const sortObj = buildLazyLoadSort(sortOptions);

        // FE#157: page from the reconciled SERVER offset (not fliterdList.length).
        isFetchingRef.current = true;
        dispatch(DBThunks.find({
            ...withoutOversizedIn(query),
            ...findObj,
            deleted: false,
            $sort: sortObj,
            $skip: fetchedOffsetRef.current,
        }))
            .unwrap()
            .then((rows: unknown) => {
                const returned = Array.isArray(rows) ? rows.length : 0;
                fetchedOffsetRef.current = advanceServerOffset(fetchedOffsetRef.current, returned);
            })
            .catch(() => { /* keep offset unchanged on failure; a retry re-requests the same page */ })
            .finally(() => { isFetchingRef.current = false; });
    };

    useEffect(() => {
        if (!featchData) return;
        
        // FE#257: identical to the display sort so page 1 matches the screen.
        const sortObj = buildLazyLoadSort(sortOptions);

        // FE#157: a new query/sort resets the reconciled offset and fetches page 1
        // from $skip:0, then seeds the offset from what the server returned.
        fetchedOffsetRef.current = 0;
        isFetchingRef.current = true;
        dispatch(DBThunks.find({
            ...withoutOversizedIn(query),
            ...findObj,
            deleted: false,
            $sort: sortObj,
            $skip: 0,
        }))
            .unwrap()
            .then((rows: unknown) => {
                fetchedOffsetRef.current = Array.isArray(rows) ? rows.length : 0;
            })
            .catch(() => { fetchedOffsetRef.current = 0; })
            .finally(() => { isFetchingRef.current = false; });
        // findObj/sortOptions are inline literals at call sites — depend on
        // their serialized keys instead of their (unstable) identities.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortKey, featchData, query, findKey, dispatch, DBThunks]);

    return (
        <div className={cn("w-full", isFullWidth && "w-full", isRelativeHeight && "h-full")}>
            {!isItemsLoaded && (
                <div className="flex justify-center items-center py-8">
                    <div className="flex flex-col gap-2">
                        <Skeleton className="h-4 w-[250px]" />
                        <Skeleton className="h-4 w-[200px]" />
                        <Skeleton className="h-4 w-[230px]" />
                    </div>
                </div>
            )}
            {isItemsLoaded && fliterdList.length > 0 && (
                <Virtuoso
                    components={{
                        Item: HeightPreservingItem,
                    }}
                    endReached={loadMore}
                    className="w-full"
                    style={{
                        height: isRelativeHeight ? '100%' : resolvedHeight,
                    }}
                    increaseViewportBy={250}
                    data={fliterdList}
                    totalCount={fliterdList.length}
                    itemContent={(index, item) => {
                        return (
                            <Component itemId={item?._id} index={index} />
                        );
                    }}
                />
            )}
            {isItemsLoaded && fliterdList.length === 0 && (
                <div className="flex flex-col justify-center items-center my-5.5">
                    <img src={noAudiencesFoundImgSrc} alt="" className="h-[120px] mb-4" />
                    <span className="text-base font-bold text-foreground dark:text-foreground">{t('bulkDashboard.noResultsFound')}</span>
                </div>
            )}
        </div>
    );
};

export default LazyLoadingVirtuoso;
